/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useMemo} from 'react'
import {useLocation, useParams} from 'react-router-dom'

// Other Hooks
import {useVariationParams} from './use-variation-params'

// Utils
import {updateSearchParams} from '@salesforce/retail-react-app/app/utils/url'
import {usePDPSearchParams} from '@salesforce/retail-react-app/app/hooks/use-pdp-search-params'
import {filterImageGroups} from '@salesforce/retail-react-app/app/utils/product-utils'
import {productUrlBuilder} from '../utils/url'
import useIsInternational from './use-is-international'
import {getVariantOrderableForLocale} from '../../../utils/intl-inventory'
/**
 * Return the first image in the `swatch` type image group for a given
 * variation value of a product.
 *
 * @param {Object} product
 * @param {Object} variationValue
 * @returns {Object} image
 */
export const getVariantValueSwatch = (product, variationValue) => {
    const {imageGroups = []} = product
    if (variationValue?.associatedSwatchURL) return variationValue.associatedSwatchURL
    return filterImageGroups(imageGroups, {
        viewType: 'swatch',
        variationValues: {
            ['color']: variationValue.value
        }
    })?.[0]?.images?.[0]
}

/**
 * Finds variation group by color value
 * @param {Object} product - The product object
 * @param {string} colorValue - The color value to search for
 * @returns {Object|null} The matching variation group or null
 */
const findVariationGroupByColor = (product, colorValue) => {
    return product?.variationGroups?.find((vg) => vg.variationValues?.color === colorValue)
}

/**
 * Resolves the correct product ID for a selected variation value.
 *
 * Logic:
 * 1. Returns `associatedPID` if present (direct mapping to a specific product).
 * 2. If attribute is "color", finds matching variation group and returns its `productId`.
 * 3. Otherwise falls back to the base `product.id`.
 *
 * This ensures correct product navigation and URL generation when selecting variations.
 *
 * @param {Object} product - Base product.
 * @param {Object} value - Selected variation value.
 * @param {Object} variationAttribute - Attribute (e.g., color, size).
 * @returns {string} Target product ID.
 */
const resolveTargetProductId = (product, value, variationAttribute) => {
    if (value?.associatedPID) {
        return value.associatedPID
    }

    if (
        variationAttribute?.id === 'color' &&
        !product?.c_isGiftCard &&
        !product?.c_isEGiftCard &&
        product?.variationGroups?.length
    ) {
        const matchingVariationGroup = findVariationGroupByColor(product, value.value)
        if (matchingVariationGroup?.productId) {
            return matchingVariationGroup.productId
        }
    }

    return product.id
}

/**
 * Constructs friendly URL for a target product ID based on master product's URL pattern
 * @param {string} targetProductId - The product ID to build URL for
 * @param {Object} product - The master product object
 * @param {Object} location - The current location object
 * @returns {string} The friendly URL path
 */
const buildFriendlyUrlForVariation = (targetProductId, product, location) => {
    if (product?.c_relativeUrl && !/^\/[^/]+\/p\//.test(location.pathname)) {
        return product.c_relativeUrl.replace(/\/[^\/]+\.html$/, `/${targetProductId}.html`)
    }
    return location.pathname.replace(/\/[^\/]+\.html$/, `/${targetProductId}.html`)
}

/**
 * Build a product href
 *
 * @param {Object} product
 * @param {Object} params
 * @param {Object} location
 * @returns {String} a product url for the current variation value.
 */
export const buildVariantValueHref = ({
    pathname,
    existingParams,
    newParams,
    product,
    productId,
    isProductPartOfSet,
    isProductPartOfBundle,
    keepCurrentPath = false,
    currentProductIdFromUrl
}) => {
    const [originalAllParams, originalProductParams] = existingParams

    // IMPORTANT: Clone params to avoid mutation across multiple href builds
    // Without cloning, params from one variation attribute leak into others
    const allParams = new URLSearchParams(originalAllParams.toString())
    const productParams = new URLSearchParams(originalProductParams.toString())

    if (isProductPartOfSet || isProductPartOfBundle) {
        updateSearchParams(productParams, newParams)
        allParams.set(productId, productParams.toString())
        return `${pathname}?${allParams.toString()}`
    } else {
        let targetVariationGroupId
        const primaryVariationAttribute = product?.variationAttributes?.[0]?.id
        const params = isProductPartOfSet || isProductPartOfBundle ? productParams : allParams

        const isPrimaryAttributeChange =
            primaryVariationAttribute &&
            newParams[primaryVariationAttribute] &&
            params.get(primaryVariationAttribute) !== newParams[primaryVariationAttribute]

        if (isPrimaryAttributeChange) {
            const matchingVariationGroup = product?.variationGroups?.find(
                (vg) =>
                    vg.variationValues?.[primaryVariationAttribute] ===
                    newParams[primaryVariationAttribute]
            )
            targetVariationGroupId = matchingVariationGroup?.productId || product?.id || productId
        } else {
            targetVariationGroupId =
                product?.variationGroups?.length > 0
                    ? product.id
                    : product?.master?.masterId || productId
        }

        // Gift card special handling: preserve color and cardType when navigating
        if (
            (product?.c_isGiftCard || product?.c_isEGiftCard) &&
            (newParams.color || newParams.cardType)
        ) {
            const currentColor = allParams.get('color')
            const currentCardType = newParams.cardType || allParams.get('cardType')

            let matchingVariationGroup

            if (newParams.color) {
                matchingVariationGroup = findGiftCardVariationGroup(
                    product,
                    'color',
                    newParams.color,
                    null,
                    currentCardType
                )
            } else if (newParams.cardType) {
                // Check if current color is available in target card type
                const targetColor = getDefaultColorForCardType(
                    product,
                    newParams.cardType,
                    currentColor
                )
                matchingVariationGroup = findGiftCardVariationGroup(
                    product,
                    'cardType',
                    newParams.cardType,
                    targetColor, // Use targetColor instead of currentColor
                    null
                )
                // Update the color param if it changed
                if (targetColor && targetColor !== currentColor) {
                    allParams.set('color', targetColor)
                }
            }

            if (matchingVariationGroup) {
                targetVariationGroupId = matchingVariationGroup.productId
            }
        }

        updateSearchParams(allParams, newParams)

        // For regular products (non-gift cards), remove color from URL params for SEO purposes
        // Color is determined by the variation group productId in the URL path
        // EXCEPT in modal/cart edit context where URL params are used to track selection state
        const isGiftCard = product?.c_isGiftCard || product?.c_isEGiftCard
        if (!isGiftCard && !keepCurrentPath) {
            allParams.delete('color')
        }

        // Only add specific variant pid if ALL variation attributes are selected
        // This prevents auto-selection of attributes when only some are chosen
        const allVariationAttributeIds = product?.variationAttributes?.map((attr) => attr.id) || []
        const allAttributesSelected = allVariationAttributeIds.every((id) => newParams[id])

        if (allAttributesSelected) {
            // Find and add specific variant pid only when all attributes are selected
            const matchingVariant = product?.variants?.find((variant) =>
                Object.keys(newParams).every((key) => variant.variationValues?.[key] === newParams[key])
            )

            if (matchingVariant?.productId) {
                allParams.set('pid', matchingVariant.productId)
            } else {
                allParams.delete('pid')
            }
        } else {
            // If not all attributes are selected, remove pid to stay on master/group product
            allParams.delete('pid')
        }

        // Helper to build URL with optional query string
        const buildUrlWithParams = (path, params) => {
            const paramString = params.toString()
            return paramString ? `${path}?${paramString}` : path
        }

        if (keepCurrentPath) {
            return buildUrlWithParams(pathname, allParams)
        }

        const currentProductId =
            currentProductIdFromUrl || pathname.split('/').pop().replace('.html', '')
        const isGiftCardColorChange =
            (product?.c_isGiftCard || product?.c_isEGiftCard) && newParams.color
        const needsPathChange =
            (isPrimaryAttributeChange && targetVariationGroupId !== currentProductId) ||
            (isGiftCardColorChange && targetVariationGroupId !== currentProductId)

        if (needsPathChange) {
            const targetVariationGroup = product?.variationGroups?.find(
                (vg) => vg.productId === targetVariationGroupId
            )
            const targetProduct = {
                id: targetVariationGroupId,
                productName: targetVariationGroup?.productName || product?.name,
                c_relativeUrl: targetVariationGroup?.c_relativeUrl
            }
            return buildUrlWithParams(productUrlBuilder(targetProduct), allParams)
        }

        return buildUrlWithParams(pathname, allParams)
    }
}

/**
 * Determine if a products variant attribute value is orderable without having to
 * load the variant in question, but filtering the list of variants with the
 * passed in attribute values.
 *
 * @param {Object} product
 * @param {Object} variationParams
 * @param {boolean} [isInternational] - True when the current locale is ESW-supported.
 * @returns
 */
export const isVariantValueOrderable = (product, variationParams, isInternational) => {
    // Safety check - if no variants, return undefined to signal we can't determine
    if (!product?.variants?.length) {
        return undefined
    }

    const filteredVariants = product.variants.filter(({variationValues}) => {
        if (!variationValues) return false
        return Object.keys(variationParams || {}).every(
            (key) => variationValues?.[key] === variationParams[key]
        )
    })

    const getVariantOrderable = (variant) => getVariantOrderableForLocale(variant, isInternational)

    // For gift cards, check if ANY variant with these variation values is orderable
    // This ensures that if all variants for a specific price value are out of stock,
    // the swatch appears disabled
    if (product?.c_isGiftCard || product?.c_isEGiftCard) {
        return filteredVariants.length > 0 && filteredVariants.some(getVariantOrderable)
    }

    // For regular products, use original logic
    return filteredVariants.some(getVariantOrderable)
}

/**
 * Determines if a product variation group with a specific color value is online.
 * @param {Object} product - The product object containing variation groups.
 * @param {Object} value - The value object containing the color to check for (expects a 'value' property).
 * @returns {boolean} True if a variation group with the specified color exists, otherwise false.
 */
const isVariationGroupOnline = (product, value) => {
    return !!product?.variationGroups?.find(
        ({variationValues}) => variationValues?.color === value?.value
    )
}

/**
 * Finds the correct variation group for gift cards based on color and card type.
 * @param {Object} product - The product object
 * @param {string} attributeId - The attribute being changed ('color' or 'cardType')
 * @param {string} newValue - The new value for the attribute
 * @param {string} currentColor - Current color value
 * @param {string} currentCardType - Current card type value
 * @returns {Object|null} The matching variation group or null
 */
const findGiftCardVariationGroup = (
    product,
    attributeId,
    newValue,
    currentColor,
    currentCardType
) => {
    if (!product?.c_isGiftCard && !product?.c_isEGiftCard) return null

    const masterProduct = product?.master || product
    const availableVariationGroups = masterProduct?.variationGroups || product?.variationGroups

    if (attributeId === 'color') {
        return availableVariationGroups?.find(
            (vg) =>
                vg.variationValues?.color === newValue &&
                vg.variationValues?.cardType === currentCardType
        )
    } else if (attributeId === 'cardType') {
        // If no current color, get the first available color for this card type
        const targetColor =
            currentColor || getDefaultColorForCardType(product, newValue, currentColor)
        return availableVariationGroups?.find(
            (vg) =>
                vg.variationValues?.cardType === newValue &&
                vg.variationValues?.color === targetColor
        )
    }

    return null
}

/**
 * Gets card types that have at least one variationGroup
 * @param {Object} product - The product object
 * @returns {Array} Array of card type values that have variationGroups
 */
const getCardTypesWithVariationGroups = (product) => {
    if (!product?.variationGroups) return []
    const cardTypes = new Set()
    product.variationGroups.forEach((vg) => {
        if (vg.variationValues?.cardType) {
            cardTypes.add(vg.variationValues.cardType)
        }
    })
    return Array.from(cardTypes)
}

/**
 * Gets available colors for a specific card type from variationGroups
 * Sorted by color value (ascending) to ensure color 1 comes first
 * @param {Object} product - The product object
 * @param {string} cardType - The card type value (e.g., 'EGC', 'CGC')
 * @returns {Array} Array of color values available for this card type
 */
const getColorsForCardType = (product, cardType) => {
    if (!product?.variationGroups) return []
    return product.variationGroups
        .filter((vg) => vg.variationValues?.cardType === cardType)
        .sort((a, b) => {
            const colorA = a.variationValues?.color || ''
            const colorB = b.variationValues?.color || ''
            return colorA.localeCompare(colorB, undefined, {numeric: true})
        })
        .map((vg) => vg.variationValues?.color)
}

/**
 * Gets the first available color for a card type, preferring current color if available
 * @param {Object} product - The product object
 * @param {string} targetCardType - The target card type
 * @param {string} currentColor - The current color value
 * @returns {string|null} The color to use for the target card type
 */
const getDefaultColorForCardType = (product, targetCardType, currentColor) => {
    const availableColors = getColorsForCardType(product, targetCardType)
    if (availableColors.includes(currentColor)) {
        return currentColor
    }
    return availableColors[0] || null
}

/**
 * Use a decorated version of a product variation attributes. This version
 * will have the following additions: which variation attribute is selected,
 * each value will have a product url, the swatch image if there is one, and
 * an updated orderable flag.
 *
 * @param {Object} product
 * @returns {Array} a decorated variation attributes list.
 *
 */
export const useVariationAttributes = (
    product = {},
    isProductPartOfSet = false,
    isProductPartOfBundle = false,
    shouldUseParams = true,
    keepCurrentPath = false
) => {
    let {variationAttributes = []} = product
    if (product?.c_associatedVariationAttributes) {
        variationAttributes = product?.c_associatedVariationAttributes
    }

    const location = useLocation()
    const {productId: currentProductIdFromUrl} = useParams()
    const isInternational = useIsInternational()

    // UPDATED: Pass shouldUseParams to useVariationParams
    const variationParams = useVariationParams(
        product,
        isProductPartOfSet,
        isProductPartOfBundle,
        shouldUseParams
    )
    
    const existingParams = usePDPSearchParams(product.id)

    // Get only URL-based params (what user has explicitly selected)
    // This excludes default values from product.variationValues
    const [allParams] = existingParams
    const urlOnlyParams = variationAttributes.reduce((acc, attr) => {
        const urlValue = allParams.get(attr.id)
        return urlValue ? {...acc, [attr.id]: urlValue} : acc
    }, {})
    const isBundleChildVariant = isProductPartOfBundle && product?.type?.variant

    // In the product bundle edit modal on the cart page, the variant ID of each bundle child is used as a key
    // for query parameters, so when a new variant is selected, a new query parameter is added since variants
    // have different IDs. The old one is not overwritten with existing logic so we remove it here
    if (isBundleChildVariant) {
        const [allParams] = existingParams
        product?.variants?.forEach(({productId: variantId}) => {
            if (variantId !== product.id && allParams.get(variantId)) {
                allParams.delete(variantId)
            }
        })
    }

    const computedResult = useMemo(
        () =>
            variationAttributes
                .filter((variationAttribute) => {
                    // Filter out cardType if only one cardType has variationGroups
                    if (
                        variationAttribute.id === 'cardType' &&
                        (product?.c_isGiftCard || product?.c_isEGiftCard)
                    ) {
                        const cardTypesWithGroups = getCardTypesWithVariationGroups(product)
                        // Hide entire cardType row if only 1 or 0 cardTypes have variationGroups
                        return cardTypesWithGroups.length > 1
                    }
                    return true
                })
                .map((variationAttribute) => ({
                    ...variationAttribute,
                    selectedValue: {
                        name: variationAttribute.values?.find(
                            ({value}) => value === variationParams?.[variationAttribute.id]
                        )?.name,
                        value: variationParams?.[variationAttribute.id]
                    },
                    values: (variationAttribute.values || [])
                        .sort((a, b) => {
                            // Detect style grouping only
                            const isStyleGrouping = !!product?.c_associatedVariationGroupIds

                            // If NOT style grouping → do not sort (keep original order)
                            if (!isStyleGrouping) return 0
                            const colorA = a.value || ''
                            const colorB = b.value || ''

                            const productA = a.productId || a.associatedPID || ''
                            const productB = b.productId || b.associatedPID || ''

                            // Sort by color first
                            const colorCompare = colorA.localeCompare(colorB, undefined, {
                                numeric: true
                            })
                            if (colorCompare !== 0) return colorCompare

                            // Then sort by productId (for same colors)
                            const productCompare = productA.localeCompare(productB)
                            if (productCompare !== 0) return productCompare

                            // Final fallback to keep order stable
                            return (a.name || '').localeCompare(b.name || '')
                        })
                        // Filter cardType: HIDE values with no variationGroups
                        .filter((value) => {
                            if (
                                (product?.c_isGiftCard || product?.c_isEGiftCard) &&
                                variationAttribute.id === 'cardType'
                            ) {
                                const cardTypesWithGroups = getCardTypesWithVariationGroups(product)
                                return cardTypesWithGroups.includes(value.value)
                            }
                            return true
                        })
                        // Filter colors: HIDE colors not in variationGroups for selected cardType
                        .filter((value) => {
                            if (
                                (product?.c_isGiftCard || product?.c_isEGiftCard) &&
                                variationAttribute.id === 'color' &&
                                variationParams?.cardType
                            ) {
                                
                                const availableColors = getColorsForCardType(
                                    product,
                                    variationParams.cardType
                                )
                                return availableColors.includes(value.value)
                            }
                            return true
                        })
                        .map((value) => {
                            const primaryVariationAttribute = product?.variationAttributes?.[0]?.id

                            // For href building (navigation), use only URL-based params for non-primary attrs
                            // This prevents auto-selecting other attributes when user hasn't chosen them
                            const hrefBaseParams =
                                variationAttribute.id === primaryVariationAttribute
                                    ? variationParams
                                    : urlOnlyParams

                            const hrefParams = {
                                ...hrefBaseParams,
                                [variationAttribute.id]: value.value
                            }

                            // For size attributes, only include sizes that have variants
                            // Use only color (primary attribute) for this check, not length
                            // This ensures sizes don't disappear when an unavailable length is selected
                            // The orderable status is calculated separately and will show sizes as unavailable
                            if (variationAttribute.id === 'size') {
                                const colorValue = variationParams?.color
                                const hasVariant = product.variants?.some(
                                    ({variationValues}) =>
                                        variationValues &&
                                        variationValues.color === colorValue &&
                                        variationValues.size === value.value
                                )
                                if (!hasVariant) return null
                            }

                            return {value, hrefParams}
                        })
                        .filter(Boolean)
                        .map(({value, hrefParams}) => {
                            const getHref = () => {
                                if (!shouldUseParams) return undefined

                                let targetProductId = resolveTargetProductId(
                                    product,
                                    value,
                                    variationAttribute
                                )

                                // Gift card special handling
                                let giftCardTargetColor = hrefParams.color || variationParams?.color
                                let giftCardTargetCardType = hrefParams.cardType || variationParams?.cardType
                                if (product?.c_isGiftCard || product?.c_isEGiftCard) {
                                    // For cardType changes, always check if current color is available
                                    // in the target cardType. If not, get the first available color.
                                    if (variationAttribute.id === 'cardType') {
                                        giftCardTargetCardType = value.value
                                        // Always recalculate color for cardType changes - current color
                                        // may not exist in the target cardType
                                        const currentColor = hrefParams.color || variationParams?.color
                                        giftCardTargetColor = getDefaultColorForCardType(
                                            product,
                                            value.value,
                                            currentColor
                                        )
                                    }
                                    // For color changes, use the new color value
                                    if (variationAttribute.id === 'color') {
                                        giftCardTargetColor = value.value
                                    }

                                    // Find matching variation group for the target cardType + color combo
                                    // This applies to ALL gift card attributes (color, cardType, priceValue, etc.)
                                    if (giftCardTargetColor && giftCardTargetCardType) {
                                        const matchingVariationGroup =
                                            product?.variationGroups?.find(
                                                (vg) =>
                                                    vg.variationValues?.cardType ===
                                                        giftCardTargetCardType &&
                                                    vg.variationValues?.color === giftCardTargetColor
                                            )
                                        if (matchingVariationGroup) {
                                            targetProductId = matchingVariationGroup.productId
                                        }
                                    }
                                }

                                // For gift cards, ALL attributes need variation group URL
                                // For regular products, only color changes need special handling
                                const isCartEdit = location.pathname.includes('/cart')
                                const isGiftCard = product?.c_isGiftCard || product?.c_isEGiftCard
                                const isVariationGroupChange =
                                    !isCartEdit &&
                                    ((variationAttribute.id === 'color' ||
                                        (isGiftCard && variationAttribute.id === 'cardType') ||
                                        (isGiftCard && targetProductId !== product.id)) &&
                                        targetProductId !== product.id)

                                if (isVariationGroupChange) {
                                    const targetVariationGroup = product?.variationGroups?.find(
                                        (vg) => vg.productId === targetProductId
                                    )
                                    // Clone to avoid mutating shared params
                                    const [originalParams] = existingParams
                                    const clonedParams = new URLSearchParams(originalParams.toString())

                                    // For regular products (non-gift cards), don't add color to URL for SEO purposes
                                    // Color is determined by the variation group productId in the URL path
                                    // Gift cards need color in URL for their multi-attribute handling
                                    if (isGiftCard) {
                                        updateSearchParams(clonedParams, {
                                            [variationAttribute.id]: hrefParams[variationAttribute.id]
                                        })

                                        // For gift cards, ensure color param is set in URL
                                        if (giftCardTargetColor) {
                                            clonedParams.set('color', giftCardTargetColor)
                                        }

                                        // For cardType changes, also set the cardType param
                                        if (giftCardTargetCardType) {
                                            clonedParams.set('cardType', giftCardTargetCardType)
                                        }
                                    } else {
                                        // For regular products on PDP, remove color from URL params
                                        clonedParams.delete('color')
                                    }

                                    clonedParams.delete('pid')

                                    const newPath =
                                        targetVariationGroup?.c_relativeUrl ||
                                        buildFriendlyUrlForVariation(
                                            targetProductId,
                                            product,
                                            location
                                        )

                                    // Return path without query string if no params remain
                                    const paramString = clonedParams.toString()
                                    return paramString ? `${newPath}?${paramString}` : newPath
                                }

                                // For other changes, use buildVariantValueHref
                                return buildVariantValueHref({
                                    pathname: location.pathname,
                                    existingParams,
                                    newParams: hrefParams,
                                    product: {...product, id: targetProductId},
                                    productId: targetProductId,
                                    isProductPartOfSet,
                                    isProductPartOfBundle,
                                    keepCurrentPath,
                                    currentProductIdFromUrl
                                })
                            }

                            // Calculate orderable based on current selections for non-color attributes
                            // Color swatches use their raw orderable value since they represent different variation groups
                            // Size/length need to check against current color+length/size combination
                            // cardType for gift cards also uses raw orderable - availability is based on variationGroups, not variants
                            let orderable = value.orderable
                            if (isInternational && variationAttribute.id === 'color') {
                                const matchingGroup = product?.variationGroups?.find(
                                    (group) => group?.variationValues?.color === value.value
                                )
                                const matchingGroupProductId =
                                    matchingGroup?.productId || matchingGroup?.id
                                const groupOrderability =
                                    product?.c_eswVariationGroupOrderability?.find(
                                        (group) => group.productId === matchingGroupProductId
                                    )

                                if (typeof groupOrderability?.orderable === 'boolean') {
                                    orderable = groupOrderability.orderable
                                } else if (typeof matchingGroup?.c_eswIsOrderableSku === 'boolean') {
                                    orderable = matchingGroup.c_eswIsOrderableSku
                                } else if (typeof matchingGroup?.orderable === 'boolean') {
                                    orderable = matchingGroup.orderable
                                }
                            }
                            const skipOrderableCalc =
                                variationAttribute.id === 'color' ||
                                ((product?.c_isGiftCard || product?.c_isEGiftCard) &&
                                    variationAttribute.id === 'cardType')
                            if (!skipOrderableCalc) {
                                // Build orderableParams using only URL-selected values + current attribute
                                // This prevents using default variationValues that may not match actual variants
                                const orderableParams = {
                                    ...urlOnlyParams,
                                    [variationAttribute.id]: value.value
                                }

                                // Only calculate if we have at least one selection (color from URL)
                                // Otherwise, use the raw orderable from API.
                                // For INTL/ESW locales we always recalculate: the native per-size
                                // value.orderable is unreliable for intl_inventory, so size/length
                                // availability must be derived from the variants' c_eswIsOrderableSku
                                // even on initial load when no color is present in the URL (the
                                // variation group already scopes the variants to the current color).
                                if (Object.keys(urlOnlyParams).length > 0 || isInternational) {
                                    const calculatedOrderable = isVariantValueOrderable(
                                        product,
                                        orderableParams,
                                        isInternational
                                    )

                                    if (calculatedOrderable !== undefined) {
                                        if (isInternational) {
                                            orderable = calculatedOrderable
                                        } else {
                                            // Only use calculated value if:
                                            // 1. We got a definitive answer (not undefined)
                                            // 2. It makes the item MORE available OR the raw value was already false
                                            // This prevents flicker where stale variant data shows everything as unavailable
                                            // If raw says orderable but calculated says not, only trust calculated
                                            // when we have reason to believe variants data is fresh (at least one variant is orderable)
                                            const hasAnyOrderableVariant = product?.variants?.some(
                                                (variant) =>
                                                    getVariantOrderableForLocale(
                                                        variant,
                                                        isInternational
                                                    )
                                            )

                                            if (
                                                calculatedOrderable ||
                                                !value.orderable ||
                                                hasAnyOrderableVariant
                                            ) {
                                                orderable = calculatedOrderable
                                            }
                                        }
                                    }
                                }
                            }

                            return {
                                ...value,
                                image: getVariantValueSwatch(product, value),
                                href: getHref(),
                                orderable,
                                online:
                                    variationAttribute.id === 'color'
                                        ? isVariationGroupOnline(product, value)
                                        : undefined
                            }
                        })
                })),
        [
            product?.id,
            shouldUseParams,
            variationParams,
            urlOnlyParams,
            location.pathname,
            isInternational
        ]
    )

    return computedResult
}
